import React, { useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, themeQuartz } from 'ag-grid-community';
import '../../config/agGridConfig';
import { adminAPI } from '../../services/api';
import CAPIFormModal from '../../components/admin/CAPIFormModal';
import Toast from '../../components/admin/Toast';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import '../../styles/AdminGlobal.css';
import './Admin.css';

interface CAPIConfig {
  id?: number;
  pixel_id: string;
  access_token: string;
  access_token_masked?: string;
  test_event_code: string;
  name: string;
  is_active: boolean;
  send_purchase_events: boolean;
  send_add_to_cart_events: boolean;
  send_initiate_checkout_events: boolean;
  send_view_content_events: boolean;
  send_lead_events: boolean;
  created_at?: string;
  updated_at?: string;
}

const FacebookCAPIManagement: React.FC = () => {
  const [configs, setConfigs] = useState<CAPIConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<CAPIConfig | null>(null);
  const [testingConnection, setTestingConnection] = useState<number | null>(null);
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({
    show: false, message: '', type: 'success'
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const columnDefs: ColDef[] = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 70, 
      sortable: true,
      cellStyle: { color: '#6b7280', fontWeight: '500' }
    },
    { 
      field: 'pixel_id', 
      headerName: 'Pixel ID', 
      sortable: true, 
      filter: true, 
      width: 160,
      cellStyle: { fontWeight: '600', color: '#1f2937' }
    },
    { 
      field: 'name', 
      headerName: 'Name', 
      sortable: true, 
      filter: true, 
      flex: 1,
      cellStyle: { color: '#374151' }
    },
    { 
      field: 'access_token_masked', 
      headerName: 'Access Token', 
      width: 180,
      cellStyle: { fontFamily: 'monospace', fontSize: '12px', color: '#6b7280' }
    },
    { 
      field: 'test_event_code', 
      headerName: 'Mode', 
      width: 120,
      cellRenderer: (params: any) => (
        <span style={{
          background: params.value ? '#fef3c7' : '#d1fae5',
          color: params.value ? '#d97706' : '#059669',
          padding: '4px 12px',
          borderRadius: '12px',
          fontWeight: '600',
          fontSize: '12px'
        }}>
          {params.value ? 'Testing' : 'Production'}
        </span>
      )
    },
    { 
      field: 'is_active', 
      headerName: 'Status', 
      width: 100,
      cellRenderer: (params: any) => (
        <span style={{
          background: params.value ? '#d1fae5' : '#fee2e2',
          color: params.value ? '#059669' : '#dc2626',
          padding: '4px 12px',
          borderRadius: '12px',
          fontWeight: '600',
          fontSize: '12px'
        }}>
          {params.value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    { 
      headerName: 'Actions',
      width: 140,
      pinned: 'right',
      lockPosition: true,
      suppressMovable: true,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        const buttonStyle = {
          border: 'none',
          borderRadius: '6px',
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '16px',
          transition: 'all 0.2s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        };
        
        return (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <button 
              onClick={() => handleTestConnection(params.data)}
              disabled={testingConnection === params.data.id}
              title="Test Connection"
              style={{ 
                ...buttonStyle,
                backgroundColor: testingConnection === params.data.id ? '#9ca3af' : '#3b82f6',
                color: 'white',
                cursor: testingConnection === params.data.id ? 'not-allowed' : 'pointer',
                opacity: testingConnection === params.data.id ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (testingConnection !== params.data.id) e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              {testingConnection === params.data.id ? '⏳' : '🧪'}
            </button>
            <button 
              onClick={() => handleEdit(params.data)}
              title="Edit CAPI Config"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#007bff',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              ✏️
            </button>
            <button 
              onClick={() => handleDeleteClick(params.data)}
              title="Delete CAPI Config"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#dc3545',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              🗑️
            </button>
          </div>
        );
      }
    }
  ];

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getFacebookCAPI();
      let data = response.data || response;
      if (data && data.results && Array.isArray(data.results)) {
        data = data.results;
      }
      const configList = Array.isArray(data) ? data : (data ? [data] : []);
      setConfigs(configList);
    } catch (error) {
      console.error('Failed to load CAPI configs:', error);
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setSelectedConfig(null);
    setIsModalOpen(true);
  };

  const handleEdit = (config: CAPIConfig) => {
    setSelectedConfig(config);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (config: CAPIConfig) => {
    setSelectedConfig(config);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedConfig) return;
    try {
      await adminAPI.deleteFacebookCAPI(selectedConfig.id!);
      setToast({ show: true, message: 'CAPI configuration deleted successfully', type: 'success' });
      loadConfigs();
    } catch (error) {
      console.error('Delete error:', error);
      setToast({ show: true, message: 'Failed to delete CAPI configuration', type: 'error' });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedConfig(null);
    }
  };

  const handleTestConnection = async (config: CAPIConfig) => {
    try {
      setTestingConnection(config.id!);
      const response = await adminAPI.testFacebookCAPI(config.id!);
      const data = response.data || response;
      if (data.status === 'success') {
        setToast({ show: true, message: data.message, type: 'success' });
      } else {
        setToast({ show: true, message: data.message, type: 'error' });
      }
    } catch (error: any) {
      console.error('Test connection error:', error);
      const message = error.response?.data?.message || 'Failed to test connection';
      setToast({ show: true, message, type: 'error' });
    } finally {
      setTestingConnection(null);
    }
  };

  const handleSave = async (formData: CAPIConfig) => {
    try {
      if (selectedConfig?.id) {
        await adminAPI.updateFacebookCAPI(selectedConfig.id, formData);
        setToast({ show: true, message: 'CAPI configuration updated successfully', type: 'success' });
      } else {
        await adminAPI.createFacebookCAPI(formData);
        setToast({ show: true, message: 'CAPI configuration created successfully', type: 'success' });
      }
      setIsModalOpen(false);
      setSelectedConfig(null);
      loadConfigs();
    } catch (error) {
      console.error('Save error:', error);
      throw error;
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredConfigs = configs.filter(config =>
    config.pixel_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    config.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-page">
      <div className="management-header">
        <div>
          <h3>Facebook Conversion API (CAPI)</h3>
          <p>Configure server-side event tracking for better conversion accuracy</p>
        </div>
        <div className="management-actions">
          <button className="btn btn-success" onClick={handleAddNew}>
            + Add CAPI Config
          </button>
          <button className="btn btn-secondary" onClick={loadConfigs}>
            🔄 Refresh
          </button>
        </div>
      </div>
      
      <div className="ag-grid-wrapper">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by Pixel ID or Name..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
        <div className="results-info">
          <span>{filteredConfigs.length}</span> configuration(s) configured
        </div>
      
        <div className="ag-grid-container">
          <AgGridReact
            rowData={filteredConfigs}
            columnDefs={columnDefs}
            theme={themeQuartz}
            defaultColDef={{ resizable: true }}
            animateRows={true}
            pagination={true}
            paginationPageSize={20}
            loading={loading}
          />
        </div>
      </div>

      {/* CAPI Form Modal */}
      <CAPIFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedConfig(null);
        }}
        onSave={handleSave}
        config={selectedConfig}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        title="Delete CAPI Configuration"
        message={`Are you sure you want to delete "${selectedConfig?.name || selectedConfig?.pixel_id}"? This will stop server-side event tracking.`}
        onConfirm={handleDelete}
        onClose={() => setIsDeleteDialogOpen(false)}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Toast Notification */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
};

export default FacebookCAPIManagement;
